import { Injectable } from '@angular/core';
import { APP_LABELS, LabelKeys } from './app-labels';

/**
 * High-performance Labels Service
 * 
 * Features:
 * - Static caching for O(1) lookup performance
 * - Dot notation support: Labels.get('common.save')
 * - Parameter interpolation: Labels.get('validation.minLength', '5')
 * - Direct object access: Labels.common.save
 * - Type-safe with IDE autocomplete
 * 
 * Usage:
 * ```typescript
 * // In component/service
 * constructor(private labels: LabelsService) {}
 * 
 * // Get label
 * this.labels.get('common.save');
 * 
 * // With parameters
 * this.labels.get('validation.minLength', '5'); // "Minimum 5 characters required"
 * 
 * // Static access (no injection needed)
 * Labels.get('common.cancel');
 * Labels.common.save;
 * ```
 */

// Static cache for O(1) lookups - flattened label map
const labelCache: Map<string, string> = new Map();
let cacheInitialized = false;

/**
 * Flattens nested object into dot notation keys
 * e.g., { common: { save: 'Save' } } => { 'common.save': 'Save' }
 */
function flattenLabels(obj: Record<string, any>, prefix = ''): void {
  for (const key in obj) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      flattenLabels(obj[key], fullKey);
    } else {
      labelCache.set(fullKey, obj[key]);
    }
  }
}

/**
 * Initialize the cache (called once on first access)
 */
function initializeCache(): void {
  if (!cacheInitialized) {
    flattenLabels(APP_LABELS);
    cacheInitialized = true;
  }
}

/**
 * Get label by key with optional parameter interpolation
 * @param key - Dot notation key (e.g., 'common.save')
 * @param params - Optional parameters to replace {0}, {1}, etc.
 */
function getLabel(key: string, ...params: (string | number)[]): string {
  initializeCache();
  
  let label = labelCache.get(key);
  
  if (label === undefined) {
    console.warn(`[Labels] Key not found: "${key}"`);
    return key; // Return key as fallback
  }
  
  // Replace parameters {0}, {1}, etc.
  if (params.length > 0) {
    params.forEach((param, index) => {
      label = label!.replace(`{${index}}`, String(param));
    });
  }
  
  return label;
}

/**
 * Get nested value by path
 */
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => current?.[key], obj);
}

/**
 * Static Labels accessor - use without injection
 * 
 * Usage:
 * ```typescript
 * Labels.get('common.save');
 * Labels.common.save;
 * Labels.format('validation.minLength', 5);
 * ```
 */
export const Labels = {
  /**
   * Get label by dot notation key
   */
  get: (key: LabelKeys | string, ...params: (string | number)[]): string => {
    return getLabel(key, ...params);
  },

  /**
   * Alias for get() with parameters - more readable for formatted strings
   */
  format: (key: LabelKeys | string, ...params: (string | number)[]): string => {
    return getLabel(key, ...params);
  },

  /**
   * Check if a label key exists
   */
  has: (key: string): boolean => {
    initializeCache();
    return labelCache.has(key);
  },

  /**
   * Get all labels for a section (e.g., 'common' returns all common labels)
   */
  getSection: <T = Record<string, any>>(section: string): T | undefined => {
    return getNestedValue(APP_LABELS, section) as T;
  },

  /**
   * Get the raw labels object
   */
  get all() {
    return APP_LABELS;
  },

  // Direct access to label sections (add more as needed)
  get app() { return APP_LABELS.app; },
  get common() { return APP_LABELS.common; },
  get premium() { return APP_LABELS.premium; },
  get example() { return APP_LABELS.example; },
};

/**
 * Injectable Labels Service
 * Use when you need dependency injection (e.g., for testing)
 */
@Injectable({
  providedIn: 'root'
})
export class LabelsService {
  /**
   * Get label by dot notation key
   * @param key - Label key (e.g., 'common.save')
   * @param params - Optional parameters for interpolation
   */
  get(key: LabelKeys | string, ...params: (string | number)[]): string {
    return Labels.get(key, ...params);
  }

  /**
   * Format a label with parameters
   * @param key - Label key
   * @param params - Parameters to replace {0}, {1}, etc.
   */
  format(key: LabelKeys | string, ...params: (string | number)[]): string {
    return Labels.format(key, ...params);
  }

  /**
   * Check if a label key exists
   */
  has(key: string): boolean {
    return Labels.has(key);
  }

  /**
   * Get all labels for a section
   */
  getSection<T = Record<string, any>>(section: string): T | undefined {
    return Labels.getSection<T>(section);
  }

  /**
   * Direct access to APP_LABELS
   */
  get labels() {
    return APP_LABELS;
  }

  // Convenient accessors (add more as needed)
  get app() { return Labels.app; }
  get common() { return Labels.common; }
  get premium() { return Labels.premium; }
  get example() { return Labels.example; }
}
